import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import { Button } from "@nosible/shared/components/ui/button";
import { Input } from "@nosible/shared/components/ui/input";
import { Label } from "@nosible/shared/components/ui/label";
import { useSession } from "@/account/lib/auth-client";
import { trpc } from "@/account/lib/trpc";
import { Building2, Check, Loader2 } from "lucide-react";
import { AccountPageHeader } from "@/account/components/AccountPageHeader";

export function CreateOrganization() {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [url, setUrl] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  const createOrg = trpc.organization.create.useMutation({
    onSuccess: (org) => {
      if (org) navigate(`/organizations/${org.slug}`);
    },
  });

  // Auto-generate slug from name if not manually edited
  useEffect(() => {
    if (!slugManuallyEdited && name) {
      const generatedSlug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .substring(0, 50);
      setSlug(generatedSlug);
    }
  }, [name, slugManuallyEdited]);

  const isLoading = createOrg.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !slug.trim() || !url.trim()) return;
    createOrg.mutate({
      name: name.trim(),
      slug: slug.trim(),
      url: url.trim(),
    });
  };

  const handleSlugChange = (value: string) => {
    setSlugManuallyEdited(true);
    // Only allow lowercase letters, numbers, and hyphens
    const sanitizedSlug = value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, "")
      .substring(0, 50);
    setSlug(sanitizedSlug);
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  return (
    <div className="account-ui-page account-form-page">
      <AccountPageHeader
        title="Create Organization"
        description="Create a workspace for shared membership and product access."
        icon={Building2}
        backTo="/organizations"
        backLabel="Back to Organizations"
      />

      <Card className="account-form-card">
        <CardHeader>
          <CardTitle>New Organization</CardTitle>
          <CardDescription>
            Create a new organization to collaborate with your team.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Organization Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Organization"
                required
                maxLength={100}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <div className="flex items-center">
                <span className="text-chromium-mist text-sm mr-1">nosible.com/org/</span>
                <Input
                  id="slug"
                  value={slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="my-organization"
                  required
                  maxLength={50}
                  className="font-mono"
                />
              </div>
              <p className="text-xs text-chromium-mist">
                Only lowercase letters, numbers, and hyphens are allowed.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="url">Website</Label>
              <Input
                id="url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com"
                required
              />
            </div>

            {createOrg.error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md">
                <p className="text-sm text-red-400">
                  {createOrg.error.message}
                </p>
              </div>
            )}

            <div className="account-form-actions">
              <Button
                type="submit"
                disabled={
                  createOrg.isPending ||
                  !name.trim() ||
                  !slug.trim() ||
                  !url.trim()
                }
                className="flex-1"
              >
                {createOrg.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Create Organization
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/organizations")}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
